import { useQuery } from '@tanstack/react-query';
import { Copy, Pencil, Trash2 } from 'lucide-react';
import { Link } from 'react-router';
import { Badge } from '../../components/ui/Badge.js';
import { Button } from '../../components/ui/Button.js';
import { type Column, DataTable } from '../../components/ui/DataTable.js';
import { ListSearch } from '../../components/ui/ListSearch.js';
import { PageHeader } from '../../components/ui/PageHeader.js';
import { RowActions } from '../../components/ui/RowActions.js';
import { useAbility } from '../../context/AbilityContext.js';
import { useAuth } from '../../context/AuthContext.js';
import { useConfirmedMutation } from '../../hooks/useConfirmedMutation.js';
import { useListControls } from '../../hooks/useListControls.js';
import { api } from '../../services/api.js';
import { RESOURCE_KEYS } from '../../services/resourceKeys.js';

/** One screenful. Matches the other paginated admin lists rather than inventing a third size. */
const PAGE_SIZE = 20;

interface PricingTemplate {
  _id: string;
  supplier_id?: string;
  name: string;
  base_price_per_day_minor?: number;
  base_price_per_hour_minor?: number;
  /**
   * 061: the real field. This interface declared `is_active: boolean` before, which the payload has
   * NEVER carried — the model stores `status` (`PricingTemplate.ts:124`) and `is_active` exists only
   * on Currency. Every row therefore read "Inactive", including both active seeded rate cards, and the
   * test that should have caught it supplied the non-existent field (FR-014).
   */
  status?: 'active' | 'inactive';
}

interface PricingResponse {
  items: PricingTemplate[];
}

interface SupplierOption {
  _id: string;
  company_name: string;
}

function formatMinorUnits(amount?: number): string {
  if (amount === undefined) return '—';
  return `RM ${(amount / 100).toFixed(2)}`;
}

const baseColumns: Column<PricingTemplate>[] = [
  { key: 'name', header: 'Template Name' },
  {
    key: 'base_price_per_day_minor',
    header: 'Daily Rate',
    render: (row) => formatMinorUnits(row.base_price_per_day_minor),
  },
  {
    key: 'base_price_per_hour_minor',
    header: 'Hourly Rate',
    render: (row) => formatMinorUnits(row.base_price_per_hour_minor),
  },
  {
    key: 'status',
    header: 'Status',
    render: (row) => (
      <Badge variant={row.status === 'active' ? 'success' : 'default'}>
        {row.status === 'active' ? 'Active' : 'Inactive'}
      </Badge>
    ),
  },
];

export function PricingTemplateListPage() {
  /**
   * 061 (FR-010, FR-012): the owner column is for a caller who owns no organisation.
   *
   * A provider's list is single-organisation by definition, so the column would add nothing — and it
   * could not be filled anyway: `GET /suppliers` demands `manage Supplier` and answers a provider 403
   * (`suppliers/routes.ts:204`). Gating the query on the same condition keeps their page from issuing
   * a request they may not make.
   */
  const { activeSupplierId } = useAuth();
  const showsOwner = !activeSupplierId;

  // 063 (FR-013): the enabled-button half of H11. A host reads the refusal above
  // and was still offered this form, whose POST answers 403. A supplier owns
  // their own rate cards, so `create` — not `manage` — is the right question.
  const ability = useAbility();
  const canCreate = ability.can('create', 'PricingTemplate');
  // 159 (#14): its own ability, checked the same way the platform checks it.
  const canDelete = ability.can('delete', 'PricingTemplate');

  /**
   * 159 (#14): retire a rate card.
   *
   * Soft delete. The platform refuses with 409 when the template is assigned to any car, and its
   * message names HOW MANY — that count is the useful part, so it reaches the operator verbatim
   * through `RowActions` rather than being restated here from a number the list does not carry.
   */
  const remove = useConfirmedMutation({
    mutationFn: (id: string) => api.delete(`/pricing-templates/${id}`),
    invalidateKey: RESOURCE_KEYS.pricingTemplate.list,
  });

  // 159: built inside the component — the previous module-level column could reach neither the
  // mutation nor the ability. Both branches of the `showsOwner` ternary below use it, unchanged.
  const actionsColumn: Column<PricingTemplate> = {
    key: 'actions',
    header: 'Actions',
    render: (row) => (
      <RowActions
        actions={[
          {
            key: 'edit',
            label: 'Edit',
            href: `/pricing/${row._id}/edit`,
            icon: <Pencil className="h-4 w-4" />,
          },
          // 362 (#1215): a CREATE prefilled from this card, the fleet's Copy (156). Offered where Add Template is.
          canCreate && {
            key: 'copy',
            label: 'Copy',
            href: `/pricing/new?from=${row._id}`,
            icon: <Copy className="h-4 w-4" />,
          },
          canDelete && {
            key: 'delete',
            label: 'Delete',
            variant: 'danger' as const,
            confirmMessage: `Remove ${row.name} from the rate cards? It stops being offered for new cars. A template still assigned to cars cannot be removed.`,
            onClick: () => remove.run(row._id),
            icon: <Trash2 className="h-4 w-4" />,
          },
        ]}
      />
    ),
  };

  // 162 (#21): a pager and a search box on a list that had neither.
  const { page, setPage, searchInput, setSearchInput, search } = useListControls();

  const listPath = (() => {
    const params = new URLSearchParams({ page: String(page), limit: String(PAGE_SIZE) });
    // Only when there is something to filter by: a cleared box must show the whole list, not an
    // empty one (FR-004).
    if (search) params.set('q', search);
    return `/pricing-templates?${params.toString()}`;
  })();

  const { data, isLoading, error } = useQuery({
    queryKey: [...RESOURCE_KEYS.pricingTemplate.list, page, search],
    queryFn: () => api.get<PricingResponse & { pages: number }>(listPath),
    // 063 (FR-010): without this a refusal is retried three times, and the
    // caller reads the empty state throughout — the refusal arrives last, if at
    // all. A 403 is not a transient fault; retrying it only delays the truth.
    retry: false,
  });

  // Same query key as the rate-card form's picker, so the two screens share one fetch.
  const { data: suppliers } = useQuery({
    queryKey: RESOURCE_KEYS.supplier.list,
    queryFn: () => api.get<{ items: SupplierOption[] }>('/suppliers'),
    enabled: showsOwner,
  });

  const supplierNames = new Map(suppliers?.items?.map((s) => [s._id, s.company_name]) ?? []);

  const columns: Column<PricingTemplate>[] = showsOwner
    ? [
        ...baseColumns,
        {
          key: 'supplier_id',
          header: 'Supplier',
          // FR-013: an organisation that can no longer be named (soft-deleted) still has to list, so
          // the stored reference is shown rather than a blank or a crash — 060's convention for a
          // rate card that no longer resolves.
          render: (row) =>
            row.supplier_id ? (supplierNames.get(row.supplier_id) ?? row.supplier_id) : '—',
        },
        actionsColumn,
      ]
    : [...baseColumns, actionsColumn];

  return (
    <div>
      <PageHeader
        title="Pricing Templates"
        description="Define hourly/daily rates and pricing rules"
        actions={
          canCreate ? (
            <Link to="/pricing/new">
              <Button>Add Template</Button>
            </Link>
          ) : undefined
        }
      />
      <ListSearch
        value={searchInput}
        onChange={setSearchInput}
        label="Search rate cards by name"
        testId="pricing-search"
      />
      <DataTable
        columns={columns}
        data={data?.items ?? []}
        loading={isLoading}
        keyExtractor={(row) => row._id}
        emptyMessage="No pricing templates found"
        // 063: a host is refused this endpoint. Before, they read "No pricing
        // templates found" — a statement about the business, made on the
        // strength of a 403.
        error={error as { status?: number } | null}
        refusedMessage="Rate cards are not available for your account."
        pagination={{
          page: page,
          totalPages: data?.pages ?? 1,
          onPageChange: setPage,
        }}
      />
    </div>
  );
}
